import { useState, useRef, useMemo, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Upload,
  UserPlus,
  Users,
  Loader2,
  AlertCircle,
  CheckCircle2,
  FileText,
  Sparkles,
  Search,
  Check,
  Mail,
  Phone,
  Tag,
} from 'lucide-react';
import {
  useCreateApprenant,
  fileToBase64,
  type ManualCreatePayload,
  type EdofOverrides,
} from '../hooks/useCreateApprenant.js';
import {
  useFormationsList,
  useOffresCatalogue,
  useFormateursList,
  useEntreprisesList,
  useLeadsList,
  type LookupItem,
} from '../hooks/useLookups.js';
import { SearchableLookup } from '../../../components/SearchableLookup.js';
import { api } from '../../../lib/api.js';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Tab = 'pdf' | 'lead' | 'manuel';

/**
 * Modal de création d'apprenant V2 (refonte 27/05/2026) — 3 modes propres :
 *  - PDF : upload → extraction → preview validée par user → création
 *  - Lead : search live → sélection → preview → conversion
 *  - Manuel : formulaire structuré avec lookups réels (Formation, Formateur,
 *    Entreprise tirés depuis Zoho via /api/v1/{formations,formateurs,entreprises})
 */
export function NouvelApprenantModal({ open, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('pdf');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();
  const { createManual, createFromPdf, createFromLead, isPending } = useCreateApprenant();
  // Track où le mousedown a commencé. Sert à NE PAS fermer le modal si l'user
  // démarre une drag-selection dans un input et relâche sur l'overlay.
  // Sans ça, sélectionner du texte fermait le modal et perdait tout. Bug
  // Rafik 28/05/2026 : "quand je sélectionne une cellule en entier, le pop-up
  // disparaît, je dois tout refaire".
  const mouseDownOnOverlay = useRef(false);

  if (!open) return null;

  const handleSuccess = (id: string, msg: string) => {
    setError(null);
    setSuccess(msg);
    setTimeout(() => {
      onClose();
      navigate(`/apprenants/${id}`);
    }, 1000);
  };

  const handleError = (e: unknown) => {
    setSuccess(null);
    setError((e as Error).message);
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        // On marque que le mousedown a démarré sur l'overlay UNIQUEMENT
        // si c'est bien l'overlay (pas une drag depuis un input du modal).
        mouseDownOnOverlay.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // Fermer SEULEMENT si mousedown + mouseup tous deux sur l'overlay.
        // Empêche la fermeture quand l'user drag-select dans un input et
        // que le relâchement mouseup arrive sur l'overlay.
        if (e.target === e.currentTarget && mouseDownOnOverlay.current) {
          onClose();
        }
        mouseDownOnOverlay.current = false;
      }}
    >
      <div className="flex max-h-[95vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-neutral-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <UserPlus className="text-primary-600 h-5 w-5" />
            <h2 className="text-lg font-bold text-neutral-900">Nouvel apprenant</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-shrink-0 gap-1 border-b border-neutral-200 px-5">
          <TabBtn
            label="PDF EDOF (CPF)"
            icon={<FileText className="h-4 w-4" />}
            active={activeTab === 'pdf'}
            onClick={() => {
              setActiveTab('pdf');
              setError(null);
            }}
          />
          <TabBtn
            label="Convertir un Lead"
            icon={<Users className="h-4 w-4" />}
            active={activeTab === 'lead'}
            onClick={() => {
              setActiveTab('lead');
              setError(null);
            }}
          />
          <TabBtn
            label="Saisie manuelle"
            icon={<UserPlus className="h-4 w-4" />}
            active={activeTab === 'manuel'}
            onClick={() => {
              setActiveTab('manuel');
              setError(null);
            }}
          />
        </div>

        {/* Content scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-3 flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>{error}</div>
            </div>
          )}
          {success && (
            <div className="mb-3 flex items-start gap-2 rounded border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-700">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>{success}</div>
            </div>
          )}

          {activeTab === 'pdf' && (
            <PdfTab
              isPending={isPending}
              onCreate={async (pdfBase64, overrides) => {
                try {
                  const r = await createFromPdf({ pdfBase64, overrides });
                  handleSuccess(r.id, r.message);
                } catch (e) {
                  handleError(e);
                }
              }}
            />
          )}
          {activeTab === 'lead' && (
            <LeadTab
              isPending={isPending}
              onCreate={async (leadId, extra) => {
                try {
                  const r = await createFromLead({ leadId, ...extra });
                  handleSuccess(r.id, r.message);
                } catch (e) {
                  handleError(e);
                }
              }}
            />
          )}
          {activeTab === 'manuel' && (
            <ManuelTab
              isPending={isPending}
              onCreate={async (payload) => {
                try {
                  const r = await createManual(payload);
                  handleSuccess(r.id, r.message);
                } catch (e) {
                  handleError(e);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────

function TabBtn({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${
        active
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-neutral-500 hover:text-neutral-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════
// ONGLET PDF — upload → preview → confirm
// ════════════════════════════════════════════════════════════════════

interface EdofExtraction {
  numeroDossier: string | null;
  nom: string | null;
  prenom: string | null;
  email: string | null;
  telephone: string | null;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  niveauScolaire: string | null;
  formation: string | null;
  certification: string | null;
  dateDebut: string | null;
  dateFin: string | null;
  heuresTotal: number | null;
  modalite: string | null;
  montantTtc: number | null;
  codeOffre: string | null;
  codeAction: string | null;
  statutCpf: string | null;
}

function PdfTab({
  isPending,
  onCreate,
}: {
  isPending: boolean;
  onCreate: (pdfBase64: string, overrides: EdofOverrides) => Promise<void>;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<EdofExtraction | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // ── Form éditable : pré-rempli avec l'extraction, l'user peut TOUT modifier
  // avant de valider l'import. Ce qui diffère de l'extraction sera envoyé en
  // overrides au backend POST /from-pdf.
  const [editable, setEditable] = useState<EdofExtraction | null>(null);

  // Drag & drop handlers — accepte 1 fichier PDF déposé
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Évite le flicker quand on drag sur un enfant
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = e.dataTransfer.files;
    if (!dropped || dropped.length === 0) return;
    const f = dropped[0];
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      setExtractError(`Format non supporté : ${f.type || f.name}. Seuls les PDF sont acceptés.`);
      return;
    }
    void handleFile(f);
  };

  const handleFile = async (f: File | null) => {
    setExtractError(null);
    setExtraction(null);
    setPdfBase64(null);
    setFile(f);
    if (!f) return;
    setExtracting(true);
    try {
      const b64 = await fileToBase64(f);
      setPdfBase64(b64);
      // On peut faire un appel preview qui extrait sans créer.
      // Pour rester simple, on extrait côté serveur via un endpoint séparé,
      // mais on n'a pas créé ce endpoint. On va donc demander preview via
      // un appel sec dédié : POST /v2/apprenants/from-pdf/preview ?
      // → Pour l'instant on extrait via le même endpoint en mode "dryRun"
      // → Non implémenté côté API : on bypass le preview et on laisse la
      //   création faire l'extraction. L'extraction sera visible dans le
      //   message de succès.
      // Solution simple : on appelle l'endpoint avec dryRun=true,
      // si pas implémenté → on garde tel quel.
      try {
        const r = await api.post<{ extraction: EdofExtraction }>(
          '/api/v1/v2/apprenants/from-pdf/preview',
          { pdfBase64: b64 },
        );
        setExtraction(r.extraction);
        // Pré-remplit le formulaire éditable avec ce qu'a vu le PDF.
        // L'user pourra ensuite tout modifier (nom → "Martine1", email → le sien).
        setEditable(r.extraction);
      } catch {
        // Endpoint preview pas dispo → on saute le preview, l'utilisateur
        // confirmera directement la création
        setExtraction(null);
        setEditable(null);
      }
    } catch (e) {
      setExtractError((e as Error).message);
    } finally {
      setExtracting(false);
    }
  };

  const handleConfirm = async () => {
    if (!pdfBase64) return;
    // Calcule les overrides : tous les champs où l'user a modifié la valeur
    // extraite. Si extraction == null (preview a échoué), on envoie tout ce
    // que l'user a saisi comme overrides.
    const overrides: EdofOverrides = {};
    if (editable) {
      for (const key of Object.keys(editable) as (keyof EdofExtraction)[]) {
        const editedVal = editable[key];
        const origVal = extraction?.[key] ?? null;
        if (editedVal !== origVal && editedVal !== null && editedVal !== '') {
          (overrides as Record<string, unknown>)[key] = editedVal;
        }
      }
    }
    await onCreate(pdfBase64, overrides);
  };

  const reset = () => {
    setFile(null);
    setPdfBase64(null);
    setExtraction(null);
    setEditable(null);
    setExtractError(null);
  };

  const updateField = <K extends keyof EdofExtraction>(key: K, value: EdofExtraction[K]) => {
    setEditable((e) => (e ? { ...e, [key]: value } : e));
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-neutral-600">
        Uploade le PDF d'inscription EDOF (Mon Compte Formation). L'extraction
        identité/formation/montant/dates se fait automatiquement.
      </p>

      {!file ? (
        <div
          onClick={() => fileInput.current?.click()}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center transition-all ${
            isDragging
              ? 'border-primary-500 bg-primary-100 scale-[1.02]'
              : 'hover:border-primary-400 hover:bg-primary-50 border-neutral-300 bg-neutral-50'
          }`}
        >
          <Upload
            className={`mb-2 h-10 w-10 transition-colors ${isDragging ? 'text-primary-600' : 'text-neutral-400'}`}
          />
          <div
            className={`text-sm font-semibold transition-colors ${isDragging ? 'text-primary-800' : 'text-neutral-700'}`}
          >
            {isDragging
              ? '✋ Lâche le fichier ici'
              : 'Glisse un PDF ici ou clique pour sélectionner'}
          </div>
          <div className="text-[10px] text-neutral-500">PDF EDOF uniquement · max ~1 MB</div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Fichier sélectionné */}
          <div className="flex items-center justify-between rounded border border-neutral-200 bg-neutral-50 p-2.5">
            <div className="flex items-center gap-2">
              <FileText className="text-primary-600 h-4 w-4" />
              <div>
                <div className="text-xs font-medium text-neutral-800">{file.name}</div>
                <div className="text-[10px] text-neutral-500">
                  {Math.round(file.size / 1024)} KB
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={reset}
              className="text-[10px] text-neutral-500 hover:text-rose-600"
            >
              Changer
            </button>
          </div>

          {extracting && (
            <div className="bg-primary-50 text-primary-700 flex items-center gap-2 rounded p-3 text-xs">
              <Loader2 className="h-4 w-4 animate-spin" />
              Extraction du PDF en cours…
            </div>
          )}

          {extractError && (
            <div className="rounded border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
              ⚠ Extraction échouée : {extractError}
            </div>
          )}

          {/* FORM ÉDITABLE de l'extraction — l'user peut TOUT modifier avant import */}
          {editable && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                    <Sparkles className="h-3 w-3" />
                    Données extraites — modifie ce qui ne va pas, puis valide
                  </div>
                  <div className="mt-0.5 text-[10px] italic text-neutral-600">
                    💡 Tu peux modifier les champs avant import. Seules les modifs seront envoyées.
                  </div>
                </div>
                {extraction && (
                  <button
                    type="button"
                    onClick={() => setEditable(extraction)}
                    className="text-[10px] font-semibold text-neutral-500 hover:text-emerald-700 hover:underline"
                  >
                    ↺ Restaurer extraction
                  </button>
                )}
              </div>
              <EdofEditableForm value={editable} original={extraction} onChange={updateField} />
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!pdfBase64 || isPending || extracting}
              className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-50"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Créer l'apprenant
            </button>
          </div>
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,.pdf"
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleFile(e.target.files?.[0] ?? null)}
        className="hidden"
      />
    </div>
  );
}

/**
 * Champ d'input EDOF — composant top-level RÉUTILISABLE.
 *
 * ⚠ IMPORTANT : ce composant DOIT être défini en dehors de
 * `EdofEditableForm`. Sinon React recrée une nouvelle référence de composant
 * à chaque render → unmount/remount l'input → focus perdu à chaque frappe.
 * (Bug constaté Rafik 28/05/2026 : "à chaque lettre, je sors du champ".)
 */
function EdofTextField({
  label,
  value,
  original,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string | number | null;
  original: string | number | null;
  onChange: (val: string | number | null) => void;
  type?: 'text' | 'email' | 'tel' | 'date' | 'number';
  placeholder?: string;
}) {
  const modified =
    original !== undefined &&
    original !== null &&
    value !== original &&
    value !== null &&
    value !== '';
  return (
    <label className="flex flex-col">
      <span className="mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wider text-neutral-500">
        {label}
        {modified && (
          <span
            title={`Modifié — PDF disait: "${String(original ?? '—')}"`}
            className="rounded bg-amber-100 px-1 py-0 text-[9px] font-bold uppercase text-amber-700"
          >
            ✎ modifié
          </span>
        )}
      </span>
      <input
        type={type}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          if (type === 'number') {
            onChange(raw === '' ? null : Number(raw));
          } else {
            onChange(raw);
          }
        }}
        placeholder={placeholder}
        className={`focus:border-primary-500 rounded border bg-white px-2 py-1 text-xs focus:outline-none ${
          modified ? 'border-amber-400 bg-amber-50/30' : 'border-neutral-300'
        }`}
      />
    </label>
  );
}

function EdofEditableForm({
  value,
  original,
  onChange,
}: {
  value: EdofExtraction;
  original: EdofExtraction | null;
  onChange: <K extends keyof EdofExtraction>(key: K, val: EdofExtraction[K]) => void;
}) {
  // Helper local : projette un (key, type) vers les props d'EdofTextField.
  // Pas de composant créé inline → pas de remount.
  const bind = <K extends keyof EdofExtraction>(field: K) => ({
    value: value[field] as string | number | null,
    original: (original?.[field] ?? null) as string | number | null,
    onChange: (v: string | number | null) => onChange(field, v as unknown as EdofExtraction[K]),
  });

  return (
    <div className="space-y-3">
      <Fieldset legend="Identité">
        <div className="grid grid-cols-2 gap-2">
          <EdofTextField label="Prénom *" placeholder="Martine" {...bind('prenom')} />
          <EdofTextField label="Nom *" placeholder="VILLARD" {...bind('nom')} />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <EdofTextField
            label="Email *"
            type="email"
            placeholder="prenom.nom@example.com"
            {...bind('email')}
          />
          <EdofTextField
            label="Téléphone"
            type="tel"
            placeholder="06 12 34 56 78"
            {...bind('telephone')}
          />
        </div>
        <div className="mt-2">
          <EdofTextField
            label="N° dossier CPF"
            placeholder="401605976412"
            {...bind('numeroDossier')}
          />
        </div>
      </Fieldset>

      {/* Adresse */}
      <Fieldset legend="Adresse">
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-12">
            <EdofTextField label="Adresse" placeholder="3 rue de la Paix" {...bind('adresse')} />
          </div>
          <div className="col-span-4">
            <EdofTextField label="Code postal" placeholder="69000" {...bind('codePostal')} />
          </div>
          <div className="col-span-8">
            <EdofTextField label="Ville" placeholder="Lyon" {...bind('ville')} />
          </div>
        </div>
      </Fieldset>

      {/* Formation */}
      <Fieldset legend="Formation">
        <div className="grid grid-cols-2 gap-2">
          <EdofTextField label="Formation (intitulé EDOF)" {...bind('formation')} />
          <EdofTextField label="Certification visée" {...bind('certification')} />
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <EdofTextField label="Niveau scolaire" placeholder="A2" {...bind('niveauScolaire')} />
          <EdofTextField label="Date début" type="date" {...bind('dateDebut')} />
          <EdofTextField label="Date fin" type="date" {...bind('dateFin')} />
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <EdofTextField
            label="Heures total"
            type="number"
            placeholder="90"
            {...bind('heuresTotal')}
          />
          <EdofTextField label="Modalité" placeholder="À distance" {...bind('modalite')} />
          <EdofTextField
            label="Montant TTC (€)"
            type="number"
            placeholder="5100"
            {...bind('montantTtc')}
          />
        </div>
      </Fieldset>

      {/* Codes EDOF */}
      <Fieldset legend="Codes EDOF / Statut CPF">
        <div className="grid grid-cols-3 gap-2">
          <EdofTextField label="Code action" {...bind('codeAction')} />
          <EdofTextField label="Code offre" {...bind('codeOffre')} />
          <EdofTextField label="Statut CPF" placeholder="Validé" {...bind('statutCpf')} />
        </div>
      </Fieldset>
    </div>
  );
}

function PreviewGrid({ items }: { items: { label: string; value: string | null }[] }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs sm:grid-cols-3">
      {items.map((it, i) => (
        <div key={i} className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-neutral-500">{it.label}</div>
          <div
            className={`truncate ${it.value ? 'font-medium text-neutral-800' : 'italic text-neutral-400'}`}
            title={it.value ?? '—'}
          >
            {it.value ?? '—'}
          </div>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ONGLET LEAD — search live → select → preview → confirm
// ════════════════════════════════════════════════════════════════════

function LeadTab({
  isPending,
  onCreate,
}: {
  isPending: boolean;
  onCreate: (
    leadId: string,
    extra: { niveau?: string; formationId?: string; statutZoho?: string },
  ) => Promise<void>;
}) {
  const { items: leads, isLoading: loadingLeads } = useLeadsList();
  const { items: formations, isLoading: loadingFormations } = useFormationsList();
  const [selectedLead, setSelectedLead] = useState<LookupItem | null>(null);
  const [niveau, setNiveau] = useState('');
  const [formationId, setFormationId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const filteredLeads = useMemo(() => {
    if (!search.trim()) return leads;
    const q = norm(search.trim());
    return leads.filter(
      (l) => norm(l.label).includes(q) || (l.sublabel && norm(l.sublabel).includes(q)),
    );
  }, [leads, search]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedLead) return;
    await onCreate(selectedLead.id, {
      niveau: niveau || undefined,
      formationId: formationId || undefined,
    });
  };

  const leadRaw = selectedLead?.raw as
    | {
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        source?: string;
        status?: string;
        formationInteresse?: string;
        notes?: string;
      }
    | undefined;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Bandeau search GROS — full width */}
      <div className="border-primary-200 bg-primary-50/30 sticky top-0 z-10 -mx-5 -mt-5 mb-0 border-b px-5 py-3">
        <div className="flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 shadow-sm">
          <Search className="h-4 w-4 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              loadingLeads
                ? 'Chargement…'
                : `Rechercher parmi ${leads.length} leads (nom, email, source, formation)…`
            }
            className="flex-1 bg-transparent text-sm placeholder:text-neutral-400 focus:outline-none"
          />
          {loadingLeads && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
          {!loadingLeads && (
            <span className="text-[10px] font-semibold text-neutral-500">
              {filteredLeads.length} / {leads.length}
            </span>
          )}
        </div>
      </div>

      {/* GROSSE liste inline — pas de dropdown, on voit la liste à plein écran */}
      <div className="rounded-lg border border-neutral-200 bg-white">
        <div className="border-b border-neutral-100 bg-neutral-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
          {loadingLeads
            ? 'Chargement des leads…'
            : selectedLead
              ? `Lead sélectionné : ${selectedLead.label}`
              : `Choisis un lead à convertir (clic sur une ligne)`}
        </div>
        <div className="max-h-[40vh] divide-y divide-neutral-100 overflow-y-auto">
          {loadingLeads ? (
            <div className="flex items-center justify-center gap-2 px-3 py-8 text-xs text-neutral-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs italic text-neutral-400">
              Aucun lead ne correspond à « {search} »
            </div>
          ) : (
            filteredLeads.map((lead) => {
              const isSel = selectedLead?.id === lead.id;
              const raw = lead.raw as
                | { email?: string; phone?: string; source?: string; status?: string }
                | undefined;
              return (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => setSelectedLead(lead)}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    isSel ? 'bg-primary-50 hover:bg-primary-100' : 'hover:bg-neutral-50'
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                      isSel ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-500'
                    }`}
                  >
                    {isSel ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      lead.label
                        .split(' ')
                        .map((w) => w[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-neutral-900">
                      {lead.label}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-neutral-600">
                      {raw?.email && (
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3 w-3 text-neutral-400" />
                          {raw.email}
                        </span>
                      )}
                      {raw?.phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3 w-3 text-neutral-400" />
                          {raw.phone}
                        </span>
                      )}
                      {raw?.source && (
                        <span className="inline-flex items-center gap-1">
                          <Tag className="h-3 w-3 text-neutral-400" />
                          {raw.source}
                        </span>
                      )}
                    </div>
                  </div>
                  {raw?.status && (
                    <span className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-600">
                      {raw.status}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Preview du lead sélectionné */}
      {selectedLead && leadRaw && (
        <div className="border-primary-200 bg-primary-50/50 rounded-lg border p-3">
          <div className="text-primary-700 mb-2 text-[10px] font-bold uppercase tracking-wider">
            Lead à convertir — détails complets
          </div>
          <PreviewGrid
            items={[
              { label: 'Prénom', value: leadRaw.firstName ?? null },
              { label: 'Nom', value: leadRaw.lastName ?? null },
              { label: 'Email', value: leadRaw.email ?? null },
              { label: 'Téléphone', value: leadRaw.phone ?? null },
              { label: 'Source', value: leadRaw.source ?? null },
              { label: 'Statut Lead', value: leadRaw.status ?? null },
              { label: "Formation d'intérêt", value: leadRaw.formationInteresse ?? null },
              { label: 'Notes', value: leadRaw.notes ?? null },
            ]}
          />
        </div>
      )}

      {/* Champs additionnels (niveau + formation) — apparaissent après sélection */}
      {selectedLead && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Niveau initial (optionnel)">
            <select
              value={niveau}
              onChange={(e) => setNiveau(e.target.value)}
              className="w-full rounded border border-neutral-300 px-2 py-1.5 text-xs"
            >
              <option value="">—</option>
              {['ALPHAB', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Formation (optionnel)">
            <SearchableLookup
              items={formations}
              value={formationId}
              onSelect={(id) => setFormationId(id)}
              placeholder={loadingFormations ? 'Chargement…' : 'Choisir une formation'}
              isLoading={loadingFormations}
            />
          </Field>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!selectedLead || isPending}
          className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-50"
        >
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Convertir en apprenant
        </button>
      </div>
    </form>
  );
}

// ════════════════════════════════════════════════════════════════════
// ONGLET MANUEL — formulaire complet avec lookups
// ════════════════════════════════════════════════════════════════════

function ManuelTab({
  isPending,
  onCreate,
}: {
  isPending: boolean;
  onCreate: (payload: ManualCreatePayload) => Promise<void>;
}) {
  const { items: offres, isLoading: loadingOffres } = useOffresCatalogue();
  const { items: formateurs, isLoading: loadingFormateurs } = useFormateursList();
  const { items: entreprises, isLoading: loadingEntreprises } = useEntreprisesList();

  const [form, setForm] = useState<ManualCreatePayload>({
    nom: '',
    prenom: '',
    email: '',
    civilite: 'Mme',
    telephone: '',
    numeroDossier: '',
    niveau: '',
    dateEntree: '',
    dateSortie: '',
    montantFormation: undefined,
    typeFinancement: 'CPF',
    lieuFormation: 'À distance',
    eleveCpf: true,
    bpfEligible: true,
    handicap: false,
    activityVertical: 'CPF (State Funded)',
    offreId: undefined,
    formateurId: undefined,
    entrepriseId: undefined,
    heuresSynchrones: undefined,
    heuresAsynchrones: undefined,
    nombreHeuresGlobal: undefined,
  });

  // Sync rapide des heures totales quand sync/async changent
  const set = <K extends keyof ManualCreatePayload>(k: K, v: ManualCreatePayload[K]) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === 'heuresSynchrones' || k === 'heuresAsynchrones') {
        const s = Number(next.heuresSynchrones ?? 0);
        const a = Number(next.heuresAsynchrones ?? 0);
        next.nombreHeuresGlobal = s + a;
      }
      return next;
    });
  };

  // Adaptation auto du segment selon le type de financement
  const handleTypeFinancement = (v: string) => {
    setForm((f) => {
      const next = { ...f, typeFinancement: v };
      if (v === 'CPF') {
        next.eleveCpf = true;
        next.activityVertical = 'CPF (State Funded)';
      } else if (v === 'OPCO' || v === 'Sous-traitance') {
        next.eleveCpf = false;
        next.activityVertical = 'Pro Non-CPF (Corporate)';
      } else if (v === 'Direct') {
        next.eleveCpf = false;
        next.activityVertical = 'Andalous e-Learning (Private)';
      }
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.email) return;
    await onCreate(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-xs">
      {/* ═══ IDENTITÉ ═══ */}
      <Fieldset legend="Identité de l'apprenant">
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-2">
            <Field label="Civilité">
              <select
                value={form.civilite}
                onChange={(e) => set('civilite', e.target.value)}
                className="w-full rounded border border-neutral-300 px-2 py-1.5"
              >
                <option value="M.">M.</option>
                <option value="Mme">Mme</option>
                <option value="Mx">Mx</option>
              </select>
            </Field>
          </div>
          <div className="col-span-5">
            <Field label="Prénom *">
              <input
                required
                type="text"
                value={form.prenom}
                onChange={(e) => set('prenom', e.target.value)}
                className="focus:border-primary-500 w-full rounded border border-neutral-300 px-2 py-1.5 focus:outline-none"
              />
            </Field>
          </div>
          <div className="col-span-5">
            <Field label="Nom *">
              <input
                required
                type="text"
                value={form.nom}
                onChange={(e) => set('nom', e.target.value.toUpperCase())}
                className="focus:border-primary-500 w-full rounded border border-neutral-300 px-2 py-1.5 font-medium uppercase focus:outline-none"
              />
            </Field>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Field label="Email *">
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value.trim())}
              placeholder="ex: prenom.nom@example.com"
              className="focus:border-primary-500 w-full rounded border border-neutral-300 px-2 py-1.5 focus:outline-none"
            />
          </Field>
          <Field label="Téléphone">
            <input
              type="tel"
              value={form.telephone ?? ''}
              onChange={(e) => set('telephone', e.target.value)}
              placeholder="06 12 34 56 78"
              className="focus:border-primary-500 w-full rounded border border-neutral-300 px-2 py-1.5 focus:outline-none"
            />
          </Field>
        </div>
        <div className="mt-2">
          <Field label="N° dossier interne (optionnel — CPF, OPCO, etc.)">
            <input
              type="text"
              value={form.numeroDossier ?? ''}
              onChange={(e) => set('numeroDossier', e.target.value)}
              placeholder="ex: 401605976412 (n° CPF) ou ID OPCO"
              className="focus:border-primary-500 w-full rounded border border-neutral-300 px-2 py-1.5 focus:outline-none"
            />
          </Field>
        </div>
      </Fieldset>

      {/* ═══ FORMATION ═══ */}
      <Fieldset legend="Formation suivie">
        <div className="grid grid-cols-2 gap-2">
          {/*
            ⭐⭐⭐ LE CHOIX ARRIVE ENFIN JUSQU'AU DOSSIER — 09/10/2026.

            Ce champ lisait le cache de l'ANCIENNE base et envoyait son
            identifiant sous `formationId` — que la route de création ne lit
            nulle part. Le serveur répondait « il manque : Formation » pendant
            qu'une formation était sélectionnée juste là.

                ⛔ UN REFUS QUI DÉSIGNE UN CHAMP QUE L'ÉCRAN N'OFFRE PAS EST
                   UNE IMPASSE : il n'y a rien à corriger où il envoie.

            ⚠️ Les DEUX applications sont servies en production : corriger
               l'autre seule aurait laissé la panne attendre ici.
          */}
          <Field label="Formation (catalogue Andalous) *">
            <SearchableLookup
              items={offres}
              value={form.offreId ?? null}
              onSelect={(id) => set('offreId', id ?? undefined)}
              placeholder={loadingOffres ? 'Chargement…' : `Rechercher (${offres.length})`}
              isLoading={loadingOffres}
            />
          </Field>
          <Field label="Niveau visé">
            <select
              value={form.niveau ?? ''}
              onChange={(e) => set('niveau', e.target.value)}
              className="w-full rounded border border-neutral-300 px-2 py-1.5"
            >
              <option value="">—</option>
              {['ALPHAB', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {/*
          ⭐⭐⭐ LE FILET DU CATALOGUE — 09/10/2026.

          Le catalogue natif ne se reconstruit QUE sur demande : une offre
          créée depuis la dernière reprise n'apparaît pas dans la liste.

              ⛔ UN ÉCRAN QUI N'OFFRE QU'UNE LISTE EST UNE IMPASSE LE JOUR OÙ
                 LA LISTE N'A PAS CE QU'ON CHERCHE.

          ⚠️ Aucune règle nouvelle : le moteur accorde déjà la priorité à un
             intitulé saisi.
        */}
        <div className="mt-2">
          <Field label="… ou l’intitulé écrit à la main, si l’offre n’est pas au catalogue">
            <input
              type="text"
              value={form.formation ?? ''}
              onChange={(e) => set('formation', e.target.value)}
              placeholder="ex : Apprendre la langue arabe"
              className="w-full rounded border border-neutral-300 px-2 py-1.5"
            />
          </Field>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <Field label="Modalité / Lieu">
            <input
              type="text"
              value={form.lieuFormation ?? ''}
              onChange={(e) => set('lieuFormation', e.target.value)}
              placeholder="À distance / Présentiel / Hybride"
              className="w-full rounded border border-neutral-300 px-2 py-1.5"
            />
          </Field>
          <Field label="Date d'entrée">
            <input
              type="date"
              value={form.dateEntree ?? ''}
              onChange={(e) => set('dateEntree', e.target.value)}
              className="w-full rounded border border-neutral-300 px-2 py-1.5"
            />
          </Field>
          <Field label="Date de sortie prévue">
            <input
              type="date"
              value={form.dateSortie ?? ''}
              onChange={(e) => set('dateSortie', e.target.value)}
              className="w-full rounded border border-neutral-300 px-2 py-1.5"
            />
          </Field>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <Field label="Heures sync (visio)">
            <input
              type="number"
              min="0"
              value={form.heuresSynchrones ?? ''}
              onChange={(e) =>
                set('heuresSynchrones', e.target.value ? Number(e.target.value) : undefined)
              }
              placeholder="ex: 30"
              className="w-full rounded border border-neutral-300 px-2 py-1.5"
            />
          </Field>
          <Field label="Heures async (Moodle)">
            <input
              type="number"
              min="0"
              value={form.heuresAsynchrones ?? ''}
              onChange={(e) =>
                set('heuresAsynchrones', e.target.value ? Number(e.target.value) : undefined)
              }
              placeholder="ex: 60"
              className="w-full rounded border border-neutral-300 px-2 py-1.5"
            />
          </Field>
          <Field label="Total (auto)">
            <input
              type="number"
              readOnly
              value={form.nombreHeuresGlobal ?? ''}
              className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-neutral-600"
            />
          </Field>
        </div>
      </Fieldset>

      {/* ═══ FINANCEMENT ═══ */}
      <Fieldset legend="Financement">
        <div className="grid grid-cols-3 gap-2">
          <Field label="Type de financement *">
            <select
              required
              value={form.typeFinancement ?? ''}
              onChange={(e) => handleTypeFinancement(e.target.value)}
              className="bg-primary-50 text-primary-900 w-full rounded border border-neutral-300 px-2 py-1.5 font-semibold"
            >
              <option value="CPF">CPF — Caisse des Dépôts</option>
              <option value="OPCO">OPCO — Plan dev compétences</option>
              <option value="Direct">Direct (particulier / entreprise)</option>
              <option value="Sous-traitance">Sous-traitance OF entrante</option>
              <option value="France Travail">France Travail</option>
            </select>
          </Field>
          <Field label="Montant total (€)">
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.montantFormation ?? ''}
              onChange={(e) =>
                set('montantFormation', e.target.value ? Number(e.target.value) : undefined)
              }
              placeholder="ex: 5100"
              className="w-full rounded border border-neutral-300 px-2 py-1.5"
            />
          </Field>
          <Field label="Segment marché">
            <select
              value={form.activityVertical ?? ''}
              onChange={(e) => set('activityVertical', e.target.value)}
              className="w-full rounded border border-neutral-300 px-2 py-1.5"
            >
              <option value="CPF (State Funded)">CPF (State Funded)</option>
              <option value="Pro Non-CPF (Corporate)">Pro Non-CPF (Corporate)</option>
              <option value="Andalous e-Learning (Private)">Andalous e-Learning (Private)</option>
            </select>
          </Field>
        </div>
        <div className="mt-2 flex flex-wrap gap-3 rounded bg-neutral-50 px-2 py-1.5">
          <label className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={!!form.eleveCpf}
              onChange={(e) => set('eleveCpf', e.target.checked)}
            />
            <span>Élève CPF</span>
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={!!form.bpfEligible}
              onChange={(e) => set('bpfEligible', e.target.checked)}
            />
            <span>BPF éligible (compte dans le BPF annuel)</span>
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={!!form.handicap}
              onChange={(e) => set('handicap', e.target.checked)}
            />
            <span>Public PSH (handicap — Qualiopi I.20)</span>
          </label>
        </div>
      </Fieldset>

      {/* ═══ ACTEURS OF ═══ */}
      <Fieldset legend="Acteurs internes / partenaires">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Formateur assigné">
            <SearchableLookup
              items={formateurs}
              value={form.formateurId ?? null}
              onSelect={(id) => set('formateurId', id ?? undefined)}
              placeholder={loadingFormateurs ? 'Chargement…' : `Rechercher (${formateurs.length})`}
              isLoading={loadingFormateurs}
            />
          </Field>
          <Field label="Entreprise (si OPCO / ST / facturation B2B)">
            <SearchableLookup
              items={entreprises}
              value={form.entrepriseId ?? null}
              onSelect={(id) => set('entrepriseId', id ?? undefined)}
              placeholder={
                loadingEntreprises ? 'Chargement…' : `Rechercher (${entreprises.length})`
              }
              isLoading={loadingEntreprises}
            />
          </Field>
        </div>
      </Fieldset>

      {/* Footer actions */}
      <div className="flex items-center justify-between border-t border-neutral-100 pt-3">
        <div className="text-[10px] text-neutral-500">
          Champs marqués * obligatoires · Tous les autres modifiables après création
        </div>
        <button
          type="submit"
          disabled={!form.nom || !form.prenom || !form.email || isPending}
          className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-50"
        >
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Créer l'apprenant
        </button>
      </div>
    </form>
  );
}

// ────────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col">
      <span className="mb-0.5 text-[10px] uppercase tracking-wider text-neutral-500">{label}</span>
      {children}
    </label>
  );
}

function Fieldset({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="rounded-lg border border-neutral-200 px-3 pb-3 pt-1.5">
      <legend className="text-primary-600 px-1.5 text-[10px] font-bold uppercase tracking-widest">
        {legend}
      </legend>
      {children}
    </fieldset>
  );
}
